(() => {
  const SELECTOR = ".om-product-gallery";

  class OmProductGalleryController {
    constructor(gallery) {
      if (gallery.dataset.omGalleryReady === "true") return;

      this.gallery = gallery;
      this.viewer = gallery.querySelector('[id^="GalleryViewer"]');
      this.mediaList = gallery.querySelector('[id^="Slider-Gallery"]');
      this.thumbnailComponent = gallery.querySelector(
        '[id^="GalleryThumbnails"]',
      );
      this.thumbnailList = gallery.querySelector('[id^="Slider-Thumbnails"]');
      this.desktopRail = gallery.querySelector(
        "[data-om-desktop-thumbnail-rail]",
      );
      this.desktopThumbnailList = gallery.querySelector(
        "[data-om-desktop-thumbnail-list]",
      );
      this.counterCurrent = gallery.querySelector("[data-om-gallery-current]");
      this.counterTotal = gallery.querySelector("[data-om-gallery-total]");
      this.previousButton = gallery.querySelector(
        "[data-om-thumbnail-previous]",
      );
      this.nextButton = gallery.querySelector("[data-om-thumbnail-next]");
      this.desktopQuery = window.matchMedia("(min-width: 990px)");
      this.desktopThumbnailSignature = "";
      this.thumbnailSyncFrame = null;

      if (!this.viewer || !this.mediaList) return;

      gallery.dataset.omGalleryReady = "true";
      this.handleSlideChanged = this.handleSlideChanged.bind(this);
      this.handleThumbnailScroll = this.handleThumbnailScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleThumbnailMutations = this.handleThumbnailMutations.bind(this);

      this.viewer.addEventListener("slideChanged", this.handleSlideChanged);
      this.rebuildDesktopRail();

      if (this.thumbnailList) {
        this.thumbnailObserver = new MutationObserver(
          this.handleThumbnailMutations,
        );
        this.thumbnailObserver.observe(this.thumbnailList, {
          attributes: true,
          attributeFilter: ["aria-current"],
          childList: true,
          subtree: true,
        });
      }

      if (this.desktopThumbnailList) {
        this.desktopThumbnailList.addEventListener(
          "scroll",
          this.handleThumbnailScroll,
          { passive: true },
        );
      }

      if (this.previousButton) {
        this.previousButton.addEventListener("click", () =>
          this.scrollThumbnails(-1),
        );
      }

      if (this.nextButton) {
        this.nextButton.addEventListener("click", () =>
          this.scrollThumbnails(1),
        );
      }

      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this.gallery);

      if (this.desktopQuery.addEventListener) {
        this.desktopQuery.addEventListener("change", this.handleResize);
      } else {
        this.desktopQuery.addListener(this.handleResize);
      }

      this.syncFromSelectedThumbnail();
      this.updateControls();
    }

    getVisibleThumbnails() {
      if (!this.thumbnailList) return [];

      return Array.from(
        this.thumbnailList.querySelectorAll("[data-target]"),
      ).filter(
        (item, index) =>
          index === 0 ||
          !item.classList.contains("thumbnail-list_item--variant"),
      );
    }

    getSelectedThumbnail() {
      if (!this.thumbnailList) return null;
      return (
        this.thumbnailList
          .querySelector(".thumbnail[aria-current]")
          ?.closest("[data-target]") || null
      );
    }

    rebuildDesktopRail() {
      if (!this.desktopThumbnailList) return;

      const visibleThumbnails = this.getVisibleThumbnails();
      const nextSignature = visibleThumbnails
        .map((item) => item.dataset.target || "")
        .join("|");

      if (
        nextSignature === this.desktopThumbnailSignature &&
        this.desktopThumbnailList.children.length === visibleThumbnails.length
      ) {
        return;
      }

      const fragment = document.createDocumentFragment();

      visibleThumbnails.forEach((sourceItem) => {
        const clone = sourceItem.cloneNode(true);
        const mediaId = sourceItem.dataset.target;
        const sourceButton = sourceItem.querySelector("button");
        const button = clone.querySelector("button");

        clone.removeAttribute("id");
        clone
          .querySelectorAll("[id]")
          .forEach((element) => element.removeAttribute("id"));

        if (button) {
          button.removeAttribute("aria-describedby");
          button.toggleAttribute(
            "aria-current",
            sourceButton?.hasAttribute("aria-current") || false,
          );
          button.addEventListener("click", () => {
            if (typeof this.gallery.setActiveMedia === "function") {
              this.gallery.setActiveMedia(mediaId, false);
            } else {
              sourceButton?.click();
            }
          });
        }

        fragment.appendChild(clone);
      });

      this.desktopThumbnailList.replaceChildren(fragment);
      this.desktopThumbnailSignature = nextSignature;

      const selectedMediaId = this.getSelectedThumbnail()?.dataset.target;
      const selectedClone = selectedMediaId
        ? this.desktopThumbnailList.querySelector(
            `[data-target="${CSS.escape(selectedMediaId)}"]`,
          )
        : null;

      if (selectedClone) {
        const selectedBottom = selectedClone.offsetTop + selectedClone.offsetHeight;
        const targetScrollTop = Math.max(
          0,
          Math.min(
            selectedClone.offsetTop,
            selectedBottom - this.desktopThumbnailList.clientHeight,
          ),
        );
        this.desktopThumbnailList.scrollTop = targetScrollTop;
      } else {
        this.desktopThumbnailList.scrollTop = 0;
      }

      this.updateControls();
    }

    handleThumbnailMutations() {
      if (this.thumbnailSyncFrame) {
        window.cancelAnimationFrame(this.thumbnailSyncFrame);
      }

      this.thumbnailSyncFrame = window.requestAnimationFrame(() => {
        this.thumbnailSyncFrame = null;
        this.rebuildDesktopRail();
        this.syncFromSelectedThumbnail(true);
      });
    }

    syncDesktopSelection(mediaId) {
      if (!this.desktopThumbnailList) return;

      this.desktopThumbnailList
        .querySelectorAll(".thumbnail")
        .forEach((button) => button.removeAttribute("aria-current"));

      this.desktopThumbnailList
        .querySelector(`[data-target="${CSS.escape(mediaId)}"] .thumbnail`)
        ?.setAttribute("aria-current", "true");
    }

    getNavigationThumbnail(mediaId) {
      const list =
        this.desktopQuery.matches && this.desktopThumbnailList
          ? this.desktopThumbnailList
          : this.thumbnailList;

      return (
        list?.querySelector(`[data-target="${CSS.escape(mediaId)}"]`) || null
      );
    }

    updateCounter(mediaId) {
      if (!this.counterCurrent || !this.counterTotal) return;

      const thumbnails = this.getVisibleThumbnails();
      if (!thumbnails.length) return;

      const index = thumbnails.findIndex(
        (item) => item.dataset.target === mediaId,
      );
      this.counterCurrent.textContent = String(index >= 0 ? index + 1 : 1);
      this.counterTotal.textContent = String(thumbnails.length);
    }

    handleSlideChanged(event) {
      const mediaId = event.detail?.currentElement?.dataset?.mediaId;
      if (!mediaId) return;

      this.updateCounter(mediaId);
      this.syncDesktopSelection(mediaId);
      this.ensureThumbnailVisible(this.getNavigationThumbnail(mediaId));
    }

    syncFromSelectedThumbnail(instant = false) {
      const selected = this.getSelectedThumbnail();
      if (!selected) return;

      this.updateCounter(selected.dataset.target);
      this.syncDesktopSelection(selected.dataset.target);
      this.ensureThumbnailVisible(
        this.getNavigationThumbnail(selected.dataset.target),
        instant,
      );
      this.updateControls();
    }

    ensureThumbnailVisible(thumbnail, instant = false) {
      if (!thumbnail) return;

      const list = thumbnail.parentElement;
      if (!list) return;

      if (this.desktopQuery.matches) {
        const itemTop = thumbnail.offsetTop;
        const itemBottom = itemTop + thumbnail.offsetHeight;
        const visibleTop = list.scrollTop;
        const visibleBottom = visibleTop + list.clientHeight;

        if (itemTop < visibleTop) {
          list.scrollTo({
            top: itemTop,
            behavior: instant ? "auto" : this.scrollBehavior(),
          });
        } else if (itemBottom > visibleBottom) {
          list.scrollTo({
            top: itemBottom - list.clientHeight,
            behavior: instant ? "auto" : this.scrollBehavior(),
          });
        }
      } else {
        const itemLeft = thumbnail.offsetLeft;
        const itemRight = itemLeft + thumbnail.offsetWidth;
        const visibleLeft = list.scrollLeft;
        const visibleRight = visibleLeft + list.clientWidth;

        if (itemLeft < visibleLeft) {
          list.scrollTo({
            left: itemLeft,
            behavior: this.scrollBehavior(),
          });
        } else if (itemRight > visibleRight) {
          list.scrollTo({
            left: itemRight - list.clientWidth,
            behavior: this.scrollBehavior(),
          });
        }
      }
    }

    scrollBehavior() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth";
    }

    scrollThumbnails(direction) {
      if (!this.desktopThumbnailList || !this.desktopQuery.matches) return;

      const firstItem =
        this.desktopThumbnailList.querySelector("[data-target]");
      const step = firstItem ? firstItem.offsetHeight + 8 : 84;
      this.desktopThumbnailList.scrollBy({
        top: direction * step * 3,
        behavior: this.scrollBehavior(),
      });
    }

    handleThumbnailScroll() {
      window.requestAnimationFrame(() => this.updateControls());
    }

    handleResize() {
      this.syncFromSelectedThumbnail();
      this.updateControls();
    }

    updateControls() {
      if (
        !this.desktopThumbnailList ||
        !this.previousButton ||
        !this.nextButton
      )
        return;

      const hasOverflow =
        this.desktopQuery.matches &&
        this.desktopThumbnailList.scrollHeight >
          this.desktopThumbnailList.clientHeight + 1;

      this.previousButton.hidden = !hasOverflow;
      this.nextButton.hidden = !hasOverflow;

      if (!hasOverflow) return;

      const maxScroll =
        this.desktopThumbnailList.scrollHeight -
        this.desktopThumbnailList.clientHeight;
      this.previousButton.disabled = this.desktopThumbnailList.scrollTop <= 1;
      this.nextButton.disabled =
        this.desktopThumbnailList.scrollTop >= maxScroll - 1;
    }
  }

  const initialize = (root = document) => {
    root
      .querySelectorAll(SELECTOR)
      .forEach((gallery) => new OmProductGalleryController(gallery));
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize());
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", (event) =>
    initialize(event.target),
  );
})();
